import {
  Box,
  CircularProgress,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { Drawer, SearchInput } from "components";
import { deserializePlaceholdersToChips } from "components/editor/email-editor";
import { useGetAllCannedNotes, useGetTicket } from "controller";
import {
  memo,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { GetTicketByIdResponse } from "shared/types/serviceDesk";
import { chatVariableChipOptions } from "./chatVariableOptions";
import { resolveCannedNoteHtml } from "./resolveCannedNoteHtml";
import { SafeHtmlRenderer } from "./SafeHtmlRenderer";

// ─── Types ───────────────────────────────────────────────────────────

interface CannedNoteItem {
  mapping_id: string;
  canned_notes_title: string;
  canned_notes_quil: string;
}

interface CannedNoteDrawerProps {
  open: boolean;
  onClose: () => void;
  currentServiceId: string;
  serviceType: string;
  originSource: string;
  isDisabled: boolean;
  onInsertHtml?: (html: string) => void;
}

// ─── Card Subcomponent ───────────────────────────────────────────────

interface CannedNoteCardProps {
  note: CannedNoteItem;
  expanded: boolean;
  onToggleExpand: () => void;
  onSelect: () => void;
}

const CannedNoteCard = memo(
  ({ note, expanded, onToggleExpand, onSelect }: CannedNoteCardProps) => {
    const theme = useTheme();
    const contentRef = useRef<HTMLDivElement | null>(null);
    const [isOverflowing, setIsOverflowing] = useState(false);

    const chipHtml = useMemo(
      () =>
        deserializePlaceholdersToChips(
          note.canned_notes_quil,
          chatVariableChipOptions,
        ),
      [note.canned_notes_quil],
    );

    useLayoutEffect(() => {
      const el = contentRef.current;
      if (!el) return;

      const measure = () => {
        // Only re-measure when the clamp is active (collapsed). When expanded,
        // scrollHeight === clientHeight, so we'd lose the overflow signal that
        // keeps "View less" visible.
        if (expanded) return;
        setIsOverflowing(el.scrollHeight - el.clientHeight > 1);
      };

      measure();

      const resizeObserver = new ResizeObserver(measure);
      resizeObserver.observe(el);
      return () => resizeObserver.disconnect();
    }, [chipHtml, expanded]);

    const handleLinkClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      onToggleExpand();
    };

    const handleLinkKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.stopPropagation();
        e.preventDefault();
        onToggleExpand();
      }
    };

    return (
      <Stack
        onClick={onSelect}
        className={expanded ? "is-expanded" : undefined}
        sx={{
          height: expanded ? "auto" : "130px",
          overflow: "hidden",
          cursor: "pointer",
          padding: "12px 16px",
          borderRadius: "8px",
          border: `1px solid ${theme.palette.neutral["010"]}`,
          backgroundColor: theme.palette.neutral.main,
          gap: "8px",
          transition: "border-color 0.15s ease",
          boxShadow: "0px 0px 4px 0px #0000000F, 0px 0px 2px 0px #0000000A",
          // View more link hidden by default
          "& .canned-note-view-more": {
            opacity: 0,
            height: "0",
            ...theme.typography["button-label"],
            color: theme.palette.primary.main,
            alignSelf: "flex-start",
            cursor: "pointer",
            userSelect: "none",
          },

          // Hover → primary border + reveal link (CSS only)
          "&:hover": {
            boxShadow: "0px 0px 4px 0px #00000029, 0px 0px 2px 0px #00000014",

            borderColor: theme.palette.neutral["015"],
          },
          "&:hover .canned-note-view-more": {
            opacity: 1,
            height: "auto",
          },
        }}
      >
        <Typography
          variant="subtitle1"
          noWrap
          sx={{ color: theme.palette.lightBg.high, flexShrink: 0 }}
        >
          {note.canned_notes_title}
        </Typography>

        <Box
          ref={contentRef}
          sx={{
            flex: 1,
            pointerEvents: "none",
            userSelect: "none",
            ...(expanded
              ? {}
              : {
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  lineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }),
          }}
        >
          <SafeHtmlRenderer showMore={false} html={chipHtml} />
        </Box>

        {isOverflowing && (
          <Box
            component="span"
            className="canned-note-view-more"
            role="button"
            tabIndex={0}
            onClick={handleLinkClick}
            onKeyDown={handleLinkKeyDown}
          >
            {expanded ? "View less" : "View more"}
          </Box>
        )}
      </Stack>
    );
  },
);

CannedNoteCard.displayName = "CannedNoteCard";

// ─── Drawer ──────────────────────────────────────────────────────────

function CannedNoteDrawer({
  open,
  onClose,
  currentServiceId,
  serviceType,
  isDisabled,
  onInsertHtml,
}: CannedNoteDrawerProps) {
  const theme = useTheme();

  const [searchQuery, setSearchQuery] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data: cannedNotesData, isLoading } = useGetAllCannedNotes({
    perPageCount: 50,
    pageNumber: 1,
    sortColumn: "canned_notes_title",
    sortDirection: "asc",
    searchQuery: searchQuery || undefined,
  });

  const isTicketService = serviceType === "ticket";
  const { data: ticketData } = useGetTicket(
    isTicketService ? currentServiceId : undefined,
  );

  const notes: CannedNoteItem[] = cannedNotesData?.result ?? [];

  const handleToggleExpand = useCallback((id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  }, []);

  const handleSelect = useCallback(
    (note: CannedNoteItem) => {
      if (isDisabled) return;
      const resolved = resolveCannedNoteHtml(
        note.canned_notes_quil,
        ticketData as GetTicketByIdResponse | undefined,
      );
      onInsertHtml?.(resolved);
      onClose();
    },
    [isDisabled, ticketData, onInsertHtml, onClose],
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      anchor="right"
      title="Canned notes"
      drawerStyle={{ width: "420px" }}
      footer={<></>}
      bodySx={{ p: 0 }}
    >
      <Stack
        sx={{
          height: "100%",
          padding: "24px",
          gap: "16px",
          overflow: "hidden",
        }}
      >
        <SearchInput
          name="cannedNoteSearch"
          placeholder="Search canned notes"
          isResponsive={false}
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />

        <Box
          sx={{
            overflowY: "scroll",
            gap: "12px",
            pr: "4px",
            flex: 1,
          }}
        >
          <Stack
            sx={{
              gap: "16px",
            }}
          >
            {isLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <CircularProgress size={24} />
              </Box>
            ) : notes.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <Typography
                  variant="body-medium"
                  sx={{ color: theme.palette.lightBg.low }}
                >
                  No canned notes found
                </Typography>
              </Box>
            ) : (
              notes.map((note) => (
                <CannedNoteCard
                  key={note.mapping_id}
                  note={note}
                  expanded={expandedId === note.mapping_id}
                  onToggleExpand={() => handleToggleExpand(note.mapping_id)}
                  onSelect={() => handleSelect(note)}
                />
              ))
            )}
          </Stack>
        </Box>
      </Stack>
    </Drawer>
  );
}

export default CannedNoteDrawer;
